"use client";

import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from "react";
import type { ValidationError } from "@/lib/workflow";

// PERFORMANCE: Store the pre-grouped Map in context instead of a function.
// Each node uses useNodeValidationErrors(id) which does a stable lookup.
// Nodes only re-render when the Map reference changes, which only happens
// when validation errors actually change (not on every render).

const EMPTY_ERRORS: ValidationError[] = [];

// Context stores the grouped-by-node Map directly
const ValidationErrorMapContext = createContext<Map<string, ValidationError[]>>(new Map());
// Separate context for the full errors array (used rarely, e.g., error count badge)
const ValidationErrorListContext = createContext<ValidationError[]>(EMPTY_ERRORS);

export function ValidationErrorProvider({
  children,
  validationErrors,
}: {
  children: ReactNode;
  validationErrors: ValidationError[];
}) {
  // Build the grouped map. useMemo ensures stable reference when validationErrors hasn't changed.
  const errorsByNode = useMemo(() => {
    if (validationErrors.length === 0) return new Map<string, ValidationError[]>();

    const map = new Map<string, ValidationError[]>();
    for (const error of validationErrors) {
      if (error.nodeId) {
        const existing = map.get(error.nodeId);
        if (existing) {
          existing.push(error);
        } else {
          map.set(error.nodeId, [error]);
        }
      }
    }
    return map;
  }, [validationErrors]);

  return (
    <ValidationErrorListContext.Provider value={validationErrors}>
      <ValidationErrorMapContext.Provider value={errorsByNode}>{children}</ValidationErrorMapContext.Provider>
    </ValidationErrorListContext.Provider>
  );
}

export function useValidationErrors() {
  const validationErrors = useContext(ValidationErrorListContext);
  const errorsByNode = useContext(ValidationErrorMapContext);
  // Stable reference: only changes when errorsByNode itself changes, preventing
  // unnecessary re-runs of useEffect/useCallback in consumers that depend on this.
  const getErrorsForNode = useCallback((nodeId: string) => errorsByNode.get(nodeId) || EMPTY_ERRORS, [errorsByNode]);
  return {
    validationErrors,
    getErrorsForNode,
    hasErrors: validationErrors.length > 0,
  };
}

/**
 * Hook for individual nodes to get their validation errors.
 * PERFORMANCE: Returns a stable empty array reference when no errors exist for this node,
 * preventing unnecessary re-renders in the node component.
 */
export function useNodeValidationErrors(nodeId: string): ValidationError[] {
  const errorsByNode = useContext(ValidationErrorMapContext);
  const errors = errorsByNode.get(nodeId);

  // Cache the previous result to return stable references
  const prevRef = useRef<{ nodeId: string; errors: ValidationError[] | undefined; result: ValidationError[] }>({
    nodeId: "",
    errors: undefined,
    result: EMPTY_ERRORS,
  });

  if (prevRef.current.nodeId === nodeId && prevRef.current.errors === errors) {
    return prevRef.current.result;
  }

  const result = errors || EMPTY_ERRORS;
  prevRef.current = { nodeId, errors, result };
  return result;
}
